import pb from '../lib/pocketbaseClient';
import { getImageSrcSet, getImageUrl } from './pbImageUrl';

// Loaded data is kept for the visit, so going back to a page or opening one that
// was prefetched (on hover or touch) shows it instantly. A failed load isn't kept.
const cache = new Map();

function cached(key, load) {
  if (!cache.has(key)) {
    const entry = { value: undefined };
    entry.promise = load().then(
      (value) => {
        entry.value = value;
        return value;
      },
      (error) => {
        cache.delete(key);
        throw error;
      }
    );
    cache.set(key, entry);
  }
  return cache.get(key).promise;
}

const peek = (key) => cache.get(key)?.value;

/** Forget loaded data, e.g. after editing photos in /admin. */
export function clearCache() {
  cache.clear();
}

// requestKey: null turns off the SDK's auto-cancelling of a pending request to the
// same endpoint, which would otherwise let a prefetch cancel the page's own load.
const noCancel = { requestKey: null };

/**
 * Everything a collection page needs: the category, its albums (each with its
 * photos, cover and date range) and all visible photos in the category.
 * `category` is null when no collection has this slug. Throws if loading fails.
 */
export function getGallery(categorySlug) {
  return cached(`gallery:${categorySlug}`, () => loadGallery(categorySlug));
}

export const peekGallery = (categorySlug) => peek(`gallery:${categorySlug}`);

async function loadGallery(categorySlug) {
  let category;
  try {
    category = await pb.collection('categories').getFirstListItem(
      pb.filter('slug = {:slug}', { slug: categorySlug }),
      noCancel
    );
  } catch (e) {
    if (e.status === 404) return { category: null, albums: [], images: [] };
    throw e;
  }

  const [images, albums] = await Promise.all([
    pb.collection('images').getFullList({
      filter: pb.filter('category_id = {:id} && is_visible = true', { id: category.id }),
      sort: '+order_index',
      ...noCancel,
    }),
    // Albums are optional: older PocketBase setups don't have the collection yet.
    pb.collection('albums').getFullList({
      filter: pb.filter('category_id = {:id}', { id: category.id }),
      sort: '+order_index',
      ...noCancel,
    }).catch(() => []),
  ]);

  const imagesWithUrls = (images || []).map(img => ({
    ...img,
    url: getImageUrl(img, 'file'),
    srcSet: getImageSrcSet(img, 'file'),
  }));

  const albumsWithImages = albums
    .map(album => {
      const albumImages = imagesWithUrls.filter(img => img.album_id === album.id);
      const dates = albumImages.map(img => img.taken_at).filter(Boolean).sort();
      const hasCover = Boolean(getImageUrl(album, 'cover_image'));
      return {
        ...album,
        images: albumImages,
        cover_image_url: hasCover ? getImageUrl(album, 'cover_image') : albumImages[0]?.url || '',
        cover_srcset: hasCover ? getImageSrcSet(album, 'cover_image') : albumImages[0]?.srcSet || '',
        date_from: dates[0] || null,
        date_to: dates[dates.length - 1] || null,
      };
    })
    .filter(album => album.images.length > 0);

  return { category, albums: albumsWithImages, images: imagesWithUrls };
}

/**
 * "November 2025", "Nov – Dec 2025" or "Dec 2025 – Jan 2026" from PocketBase dates.
 * Capture times are stored as camera wall-clock time, so format them in UTC.
 */
export function formatAlbumDates(from, to) {
  if (!from) return '';
  const parse = value => new Date(value.replace(' ', 'T'));
  const start = parse(from);
  const end = parse(to || from);
  const format = (date, options) => date.toLocaleDateString('en-GB', { ...options, timeZone: 'UTC' });
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  if (sameYear && start.getUTCMonth() === end.getUTCMonth()) {
    return format(start, { month: 'long', year: 'numeric' });
  }
  if (sameYear) {
    return `${format(start, { month: 'short' })} – ${format(end, { month: 'short', year: 'numeric' })}`;
  }
  return `${format(start, { month: 'short', year: 'numeric' })} – ${format(end, { month: 'short', year: 'numeric' })}`;
}

export const countPhotos = (n) => `${n} ${n === 1 ? 'photo' : 'photos'}`;

/** All collections with their cover URLs. Throws if loading fails. */
export function getAllCategories() {
  return cached('categories', async () => {
    const data = await pb.collection('categories').getFullList({
      sort: '+order_index',
      ...noCancel,
    });

    return (data || []).map(category => ({
      ...category,
      cover_image_url: getImageUrl(category, 'cover_image'),
      cover_srcset: getImageSrcSet(category, 'cover_image'),
    }));
  });
}

export const peekCategories = () => peek('categories');
